import { useMutation, useQueryClient } from 'react-query'
import { BASE_API_URL } from 'constants/env'
import { API_METHODS } from 'constants/api-methods'
import { apiHeaders } from 'utils/api-headers'
import { store } from 'index'
import { handleErrors } from 'utils/handle-errors'
import { PhysicalPhone, PhoneStatus } from 'interfaces/physical-phone'
import toast from 'react-hot-toast'

export interface CreatePhysicalPhoneParams {
  terminalId: string
  deviceModel: string
  serialNumber: string
  imei: string
  count?: number
  names: string[] // Generated names to use
}

interface CreatePhysicalPhoneRequest {
  name: string
  deviceModel: string
  serialNumber: string
  imei?: string
  terminalId: string
  macMiniUserId?: string
  status: PhoneStatus
}

const createSinglePhysicalPhone = async (
  phoneData: CreatePhysicalPhoneRequest
): Promise<PhysicalPhone> => {
  let response = await fetch(
    `${BASE_API_URL}/api/v4/phone_number_service/physical_phones`,
    {
      method: API_METHODS.POST,
      headers: apiHeaders(store.getState()),
      body: JSON.stringify(phoneData),
    }
  )
  response = handleErrors(response)
  return response.json()
}

const createPhysicalPhones = async (
  params: CreatePhysicalPhoneParams
): Promise<PhysicalPhone[]> => {
  const { names, terminalId, deviceModel, serialNumber, imei } = params

  // Create single request (count is always 1)
  const request: CreatePhysicalPhoneRequest = {
    name: names[0], // Use first (and only) name
    deviceModel,
    serialNumber, // Use exact serial number as entered
    imei: imei, // Use exact IMEI as entered
    terminalId, // Include terminal ID for direct relationship
    macMiniUserId: undefined, // RECEIVED devices are not assigned to mac mini user
    status: PhoneStatus.RECEIVED,
  }

  // Execute single request
  const result = await createSinglePhysicalPhone(request)
  return [result] // Return array for consistency with existing interface
}

const useCreatePhysicalPhone = () => {
  const queryClient = useQueryClient()

  return useMutation(createPhysicalPhones, {
    onSuccess: (data) => {
      const count = data.length
      toast.success(
        `Successfully created ${count} Physical Phone${count > 1 ? 's' : ''}`
      )

      // Invalidate related queries
      queryClient.invalidateQueries(['terminals'])
      queryClient.invalidateQueries(['received-physical-phones'])
      queryClient.invalidateQueries(['received-physical-phones-count'])
      queryClient.invalidateQueries(['terminal-detail'])
      queryClient.invalidateQueries(['next-device-names'])
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })
}

export default useCreatePhysicalPhone
